<app-main-layout>
  @if (order) {
    <div class="bg-gray-50 min-h-screen">
      <div class="px-6 py-6 max-w-[1700px] mx-auto">

        <!-- Header -->
        <div class="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div class="flex items-center gap-4">
            <button [routerLink]="['/sales']" class="h-10 w-10 flex-shrink-0 rounded-full bg-white border border-gray-200 shadow-sm flex items-center justify-center text-gray-500 hover:text-gray-900 hover:border-amber-300 transition-colors" matTooltip="Back to sales">
              <mat-icon>arrow_back</mat-icon>
            </button>
            <div>
              <div class="flex flex-wrap items-center gap-3">
                <h1 class="text-2xl font-bold text-gray-900 tracking-tight">Order #{{ order.orderNo }}</h1>
                <span class="text-xs font-semibold px-3 py-1 rounded-full capitalize" [ngClass]="statusPill(order.orderStatus)">{{ order.orderStatus | lowercase }}</span>
                @if (order.paymentMethod) {
                  <span class="flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-gray-100 text-gray-700 capitalize">
                    <mat-icon class="!text-[14px] !h-[14px] !w-[14px]">credit_card</mat-icon>{{ order.paymentMethod | lowercase }}
                  </span>
                }
              </div>
              <div class="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-500">
                <span>Placed on {{ order.orderDate | date: 'MMM d, y, HH:mm' }}</span>
                @if (order.paymentDate) {
                  <span class="flex items-center gap-1 text-emerald-700 font-medium">
                    <mat-icon class="!text-[15px] !h-[15px] !w-[15px]">check_circle</mat-icon> Paid {{ order.paymentDate | date: 'MMM d, HH:mm' }}
                  </span>
                } @else if (order.orderStatus !== 'CANCELLED' && order.orderStatus !== 'VOID' && order.orderStatus !== 'FAILED') {
                  <span class="flex items-center gap-1 text-amber-700 font-medium">
                    <mat-icon class="!text-[15px] !h-[15px] !w-[15px]">schedule</mat-icon> Awaiting payment
                  </span>
                }
                <button class="flex items-center gap-1 text-xs font-semibold text-gray-400 hover:text-gray-800 transition-colors" (click)="copyText(order.orderNo, 'Order number')" matTooltip="Copy order number">
                  <mat-icon class="!text-[14px] !h-[14px] !w-[14px]">content_copy</mat-icon> Copy no.
                </button>
              </div>
            </div>
          </div>
          <div class="flex flex-wrap items-center gap-3">
            @if (navPosition; as pos) {
              <div class="flex items-center bg-white border border-gray-100 shadow-sm rounded-2xl p-1">
                <button class="flex items-center gap-0.5 pl-2 pr-3 py-2 rounded-xl text-sm font-semibold text-gray-700 hover:bg-amber-50 hover:text-gray-900 transition-colors disabled:opacity-30 disabled:hover:bg-transparent disabled:cursor-not-allowed"
                  [disabled]="!hasPrevOrder || navigating" (click)="goToOrder(-1)" matTooltip="Previous order in the list">
                  <mat-icon class="!text-[20px] !h-5 !w-5">chevron_left</mat-icon> Previous
                </button>
                <span class="px-3 text-xs font-semibold text-gray-500 whitespace-nowrap border-x border-gray-100">{{ pos.current }} of {{ pos.total }}</span>
                <button class="flex items-center gap-0.5 pl-3 pr-2 py-2 rounded-xl text-sm font-semibold text-gray-700 hover:bg-amber-50 hover:text-gray-900 transition-colors disabled:opacity-30 disabled:hover:bg-transparent disabled:cursor-not-allowed"
                  [disabled]="!hasNextOrder || navigating" (click)="goToOrder(1)" matTooltip="Next order in the list">
                  Next <mat-icon class="!text-[20px] !h-5 !w-5">chevron_right</mat-icon>
                </button>
              </div>
            }
            <div class="flex items-center gap-6 bg-white border border-gray-100 shadow-sm rounded-2xl px-6 py-3">
              <div>
                <div class="text-xs font-semibold text-gray-400 uppercase tracking-wide">Order total</div>
                <div class="text-2xl font-bold text-gray-900">£{{ order.netAmount | number:'1.2-2' }}</div>
              </div>
              @if (totalRefundedAmount > 0) {
                <div class="pl-6 border-l border-gray-100">
                  <div class="text-xs font-semibold text-gray-400 uppercase tracking-wide">Net balance</div>
                  <div class="text-xl font-bold text-emerald-700">£{{ (order.netAmount - totalRefundedAmount) | number:'1.2-2' }}</div>
                  <div class="text-xs text-red-600 font-semibold">£{{ totalRefundedAmount | number:'1.2-2' }} refunded</div>
                </div>
              }
            </div>
          </div>
        </div>

        <div class="grid grid-cols-1 xl:grid-cols-4 gap-6 items-start">
          <div class="xl:col-span-3 flex flex-col gap-6 min-w-0">

            <!-- Summary and actions -->
            <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">

              <div class="lg:col-span-2 bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                <div class="flex items-center gap-2 mb-5">
                  <mat-icon class="!text-[20px] !h-5 !w-5 !text-amber-500">receipt_long</mat-icon>
                  <h2 class="font-semibold text-gray-800">Summary</h2>
                </div>

                <!-- Progress -->
                <div class="flex items-start mb-6">
                  @for (step of orderSteps; track $index; let last = $last) {
                    <div class="flex flex-col items-center text-center" [class.flex-1]="!last" [class.flex-none]="last">
                      <div class="flex items-center w-full">
                        <div class="h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 border-2 transition-colors"
                          [ngClass]="step.negative ? 'bg-red-500 border-red-500 text-white' : step.done ? 'bg-emerald-500 border-emerald-500 text-white' : 'bg-white border-gray-200 text-gray-300'">
                          <mat-icon class="!text-[18px] !h-[18px] !w-[18px]">{{ step.negative ? 'close' : step.done ? 'check' : 'more_horiz' }}</mat-icon>
                        </div>
                        @if (!last) {
                          <div class="h-0.5 flex-1 mx-2 rounded" [ngClass]="orderSteps[$index + 1].done || orderSteps[$index + 1].negative ? 'bg-emerald-400' : 'bg-gray-200'"></div>
                        }
                      </div>
                      <div class="mt-1.5 text-xs font-semibold" [ngClass]="step.negative ? 'text-red-600' : step.done ? 'text-gray-800' : 'text-gray-400'">{{ step.label }}</div>
                      <div class="text-[11px] text-gray-400 min-h-[16px]">{{ step.date ? (step.date | date: 'MMM d, HH:mm') : '' }}</div>
                    </div>
                  }
                </div>

                <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  @if (order.deliveryMethod) {
                    <div class="rounded-xl bg-gray-50 border border-gray-100 px-4 py-3">
                      <div class="flex items-center gap-1.5 text-[11px] font-semibold text-gray-400 uppercase tracking-wide"><mat-icon class="!text-[15px] !h-[15px] !w-[15px]">local_shipping</mat-icon> Delivery method</div>
                      @if (!modifyDelivery) {
                        <div class="mt-1.5 flex items-center gap-2">
                          <span class="flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full"
                            [ngClass]="order.deliveryMethod === 'COLLECTION' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'">
                            {{ order.deliveryMethod }}
                          </span>
                          @if (!modifyDelivery && editDeliveryVisible && (order.orderStatus === 'PENDING' || order.orderStatus === 'ON HOLD' || order.orderStatus === 'PENDING COLLECTION')) {
                            <button class="text-xs font-semibold text-amber-600 hover:text-amber-800 underline" (click)="onModifyDelivery()"
                            >Edit</button>
                          }
                        </div>
                      } @else {
                        <div class="mt-1.5 flex flex-wrap items-center gap-2">
                          <select class="px-2 py-1.5 rounded-lg border border-gray-200 bg-white text-sm" [(ngModel)]="selectedDeliveryMethod" (ngModelChange)="onDeliveryMethodUpdate()">
                            @for (m of deliveryMethodOptions; track m) {
                              <option [value]="m">{{ m }}</option>
                            }
                          </select>
                          <button class="px-3 py-1.5 rounded-lg bg-amber-400 text-neutral-900 text-xs font-semibold hover:bg-amber-300" (click)="saveOrder(false,true)">Save</button>
                          <button class="px-3 py-1.5 rounded-lg bg-gray-100 text-gray-600 text-xs font-semibold hover:bg-gray-200" (click)="onCancelDeliveryChanges()">Cancel</button>
                        </div>
                      }
                    </div>
                  }

                  @if (order.deliveryTerms) {
                    <div class="rounded-xl bg-gray-50 border border-gray-100 px-4 py-3">
                      <div class="flex items-center gap-1.5 text-[11px] font-semibold text-gray-400 uppercase tracking-wide"><mat-icon class="!text-[15px] !h-[15px] !w-[15px]">schedule</mat-icon> Delivery terms</div>
                      <div class="mt-1.5 font-medium text-gray-800 leading-snug">{{ order.deliveryTerms }}</div>
                    </div>
                  }

                  @if (order.userId) {
                    <div class="rounded-xl bg-gray-50 border border-gray-100 px-4 py-3">
                      <div class="flex items-center gap-1.5 text-[11px] font-semibold text-gray-400 uppercase tracking-wide"><mat-icon class="!text-[15px] !h-[15px] !w-[15px]">support_agent</mat-icon> Agent</div>
                      @if (!modifyAgent) {
                        <div class="mt-1.5 flex items-center gap-2">
                          <span class="h-6 w-6 rounded-full bg-amber-100 text-amber-800 text-[11px] font-bold flex items-center justify-center">{{ (agentName || '?')[0] | uppercase }}</span>
                          <span class="font-medium text-gray-800">{{ agentName || 'Not assigned' }}</span>
                          @if (!modifyAgent && editAgentVisible) {
                            <button class="ml-1 text-xs font-semibold text-amber-600 hover:text-amber-800 underline" (click)="onModifyAgent()">Edit</button>
                          }
                        </div>
                      } @else {
                        <div class="mt-1.5 flex flex-wrap items-center gap-2">
                          <select class="px-2 py-1.5 rounded-lg border border-gray-200 bg-white text-sm" name="agent" [(ngModel)]="agent">
                            <option value="0">All</option>
                            @if (agents) {
                              @for (ag of agents; track ag) {
                                <option [value]="ag.userid">{{ag.firstname}}</option>
                              }
                            }
                          </select>
                          <button class="px-3 py-1.5 rounded-lg bg-amber-400 text-neutral-900 text-xs font-semibold hover:bg-amber-300" (click)="updateAgent()">Save</button>
                          <button class="px-3 py-1.5 rounded-lg bg-gray-100 text-gray-600 text-xs font-semibold hover:bg-gray-200" (click)="onCancelAgentChanges()">Cancel</button>
                        </div>
                      }
                    </div>
                  }

                  @if (order.deliveryMethod === 'COLLECTION') {
                    <div class="rounded-xl bg-gray-50 border border-gray-100 px-4 py-3">
                      <div class="flex items-center gap-1.5 text-[11px] font-semibold text-gray-400 uppercase tracking-wide"><mat-icon class="!text-[15px] !h-[15px] !w-[15px]">storefront</mat-icon> Collection point</div>
                      <div class="mt-1.5 font-medium text-gray-800">{{ order.store }}</div>
                    </div>
                  }

                  @if (order.origin) {
                    <div class="rounded-xl bg-gray-50 border border-gray-100 px-4 py-3">
                      <div class="flex items-center gap-1.5 text-[11px] font-semibold text-gray-400 uppercase tracking-wide"><mat-icon class="!text-[15px] !h-[15px] !w-[15px]">campaign</mat-icon> Came from</div>
                      <div class="mt-1.5 font-medium text-gray-800">{{ originLabel(order.origin) }}</div>
                    </div>
                  }

                  @if (order.vatNumber) {
                    <div class="rounded-xl bg-gray-50 border border-gray-100 px-4 py-3">
                      <div class="flex items-center gap-1.5 text-[11px] font-semibold text-gray-400 uppercase tracking-wide"><mat-icon class="!text-[15px] !h-[15px] !w-[15px]">badge</mat-icon> VAT number</div>
                      <div class="mt-1.5 font-medium text-gray-800">{{ order.vatNumber }}</div>
                    </div>
                  }

                  @if (order.paymentMethod === 'PAYPAL') {
                    <div class="sm:col-span-2 rounded-xl bg-blue-50/40 border border-blue-100 px-4 py-3">
                      <div class="flex items-center gap-1.5 text-[11px] font-semibold text-gray-400 uppercase tracking-wide"><mat-icon class="!text-[15px] !h-[15px] !w-[15px]">local_shipping</mat-icon> PayPal tracking</div>
                      @if (!modifyTracking) {
                        <div class="mt-1.5 flex flex-wrap items-center gap-2">
                          @if (order.trackingNumber) {
                            <span class="font-medium text-gray-800">{{ order.trackingMethod }} - {{ order.trackingNumber }}</span>
                            <span class="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-blue-100 text-blue-700">{{ order.trackingStatus }}</span>
                          } @else {
                            <span class="font-medium text-gray-500">Not set</span>
                          }
                          <button class="text-xs font-semibold text-amber-600 hover:text-amber-800 underline" (click)="onModifyTracking()">Edit</button>
                          @if (canCheckPayment) {
                            <button class="ml-auto flex items-center gap-1 px-2.5 py-1 rounded-lg bg-white border border-blue-200 text-xs font-semibold text-blue-700 hover:bg-blue-50 disabled:opacity-50"
                              [disabled]="paypalTrackingChecking" (click)="onVerifyPaypalTracking()" matTooltip="Ask PayPal what tracking it holds for this order (super admins, read-only)">
                              @if (paypalTrackingChecking) {
                                <span class="inline-block h-3 w-3 rounded-full border-2 border-blue-400 border-t-transparent animate-spin"></span> Checking...
                              } @else {
                                <mat-icon class="!text-[15px] !h-[15px] !w-[15px]">fact_check</mat-icon> Verify at PayPal
                              }
                            </button>
                          }
                        </div>
                        @if (paypalTrackingCheck; as c) {
                          @if (paypalVerdicts[c.verdict]; as v) {
                            <div class="mt-3 rounded-lg border px-3 py-2.5 text-xs" [class]="v.cls">
                              <div class="flex items-center justify-between gap-2">
                                <span class="flex items-center gap-1.5 font-bold text-sm"><mat-icon class="!text-[17px] !h-[17px] !w-[17px]">{{ v.icon }}</mat-icon>{{ v.label }}</span>
                                <span class="opacity-70">checked {{ c.checkedAt | date:'d MMM, HH:mm:ss' }}</span>
                              </div>
                              <p class="mt-1">{{ v.text }}@if (c.error) { <span> ({{ c.error }})</span> }</p>
                              @if (c.paypal?.all?.length) {
                                <table class="mt-2 w-full">
                                  <thead><tr class="text-left opacity-70"><th class="font-semibold pr-3 py-0.5"></th><th class="font-semibold pr-3">Tracking no.</th><th class="font-semibold pr-3">Status</th><th class="font-semibold">Carrier</th></tr></thead>
                                  <tbody>
                                    <tr><td class="pr-3 py-0.5 font-semibold">Ours</td><td class="pr-3">{{ c.ours.trackingNumber || '-' }}</td><td class="pr-3">{{ c.ours.expected || c.ours.trackingStatus || '-' }}</td><td></td></tr>
                                    @for (t of c.paypal.all; track $index) {
                                      <tr><td class="pr-3 py-0.5 font-semibold">PayPal</td><td class="pr-3">{{ t.trackingNumber || '-' }}</td><td class="pr-3 font-semibold">{{ t.status || '-' }}</td><td>{{ t.carrier || '-' }}</td></tr>
                                    }
                                  </tbody>
                                </table>
                              }
                            </div>
                          }
                        }
                      } @else {
                        <div class="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-3">
                          <div>
                            <label class="text-xs text-gray-500">Method</label>
                            <select class="mt-1 w-full px-2 py-1.5 rounded-lg border border-gray-200 bg-white text-sm" [(ngModel)]="selectedTrackingMethod">
                              <option [value]="'NST TRACKING'">NST TRACKING</option>
                            </select>
                          </div>
                          <div>
                            <label class="text-xs text-gray-500">Tracking no.</label>
                            <input type="text" class="mt-1 w-full px-2 py-1.5 rounded-lg border border-gray-200 bg-white text-sm" [(ngModel)]="selectedTrackingNumber" />
                          </div>
                          <div>
                            <label class="text-xs text-gray-500">Status</label>
                            <select class="mt-1 w-full px-2 py-1.5 rounded-lg border border-gray-200 bg-white text-sm" [(ngModel)]="selectedTrackingStatus">
                              <option [value]="'NOT SHIPPED'">NOT SHIPPED</option>
                              <option [value]="'DISPATCHED'">DISPATCHED</option>
                              <option [value]="'IN TRANSIT'">IN TRANSIT</option>
                              <option [value]="'DELIVERED'">DELIVERED</option>
                            </select>
                          </div>
                          <div class="sm:col-span-3 flex gap-2">
                            <button class="px-3 py-1.5 rounded-lg bg-amber-400 text-neutral-900 text-xs font-semibold hover:bg-amber-300" (click)="onSaveTracking()">Save</button>
                            <button class="px-3 py-1.5 rounded-lg bg-gray-100 text-gray-600 text-xs font-semibold hover:bg-gray-200" (click)="onCancelTrackingChanges()">Cancel</button>
                          </div>
                        </div>
                      }
                    </div>
                  }
                </div>
              </div>

              <!-- Actions -->
              <div class="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                <div class="flex items-center gap-2 mb-4">
                  <mat-icon class="!text-[20px] !h-5 !w-5 !text-amber-500">bolt</mat-icon>
                  <h2 class="font-semibold text-gray-800">Actions</h2>
                </div>
                <div class="flex flex-col gap-2 text-sm">
                  @if (order.orderStatus !== 'PENDING') {
                    <button class="flex items-center gap-2 px-3 py-2.5 rounded-xl border border-gray-200 hover:border-amber-300 hover:bg-amber-50/50 font-semibold text-gray-700 transition-colors text-left"
                      (click)="onEmailOrder()">
                      <mat-icon class="!text-[18px] !h-[18px] !w-[18px] !text-amber-500">mail</mat-icon> Email order
                    </button>
                  }
                  @if (canOpenDeliveryValidation) {
                    <a class="flex items-center gap-2 px-3 py-2.5 rounded-xl border border-gray-200 hover:border-amber-300 hover:bg-amber-50/50 font-semibold text-gray-700 transition-colors text-left"
                      [routerLink]="['/delivery-validation', order.orderNo]" matTooltip="Open this order on the delivery validation screen">
                      <mat-icon class="!text-[18px] !h-[18px] !w-[18px] !text-amber-500">local_shipping</mat-icon> Delivery validation
                      <mat-icon class="!text-[18px] !h-[18px] !w-[18px] ml-auto text-gray-300">chevron_right</mat-icon>
                    </a>
                  }
                  <button class="flex items-center gap-2 px-3 py-2.5 rounded-xl border border-gray-200 hover:border-amber-300 hover:bg-amber-50/50 font-semibold text-gray-700 transition-colors text-left"
                    (click)="onInvoiceDownload()">
                    <mat-icon class="!text-[18px] !h-[18px] !w-[18px] !text-amber-500">download</mat-icon> Download invoice
                  </button>
                  @if (order.orderStatus === 'PENDING' || order.orderStatus === 'ON HOLD' || order.orderStatus === 'PENDING COLLECTION') {
                    <button class="flex items-center gap-2 px-3 py-2.5 rounded-xl border border-gray-200 hover:border-amber-300 hover:bg-amber-50/50 font-semibold text-gray-700 transition-colors text-left"
                      (click)="onSendPaymentLink()">
                      <mat-icon class="!text-[18px] !h-[18px] !w-[18px] !text-amber-500">link</mat-icon> Send payment link
                      @if (sendPayLinkProgress) {
                        <span class="ml-auto"><mat-spinner diameter="16"></mat-spinner></span>
                      }
                    </button>
                    @if (paymentLink) {
                      <button class="flex items-center gap-2 px-3 py-2.5 rounded-xl border font-semibold transition-colors text-left"
                        [ngClass]="payLinkCopied ? 'border-emerald-300 bg-emerald-50 text-emerald-700' : 'border-gray-200 hover:border-amber-300 hover:bg-amber-50/50 text-gray-700'"
                        (click)="onCopyPaymentLink()" [title]="paymentLink">
                        <mat-icon class="!text-[18px] !h-[18px] !w-[18px]" [ngClass]="payLinkCopied ? '!text-emerald-600' : '!text-amber-500'">{{ payLinkCopied ? 'check' : 'content_copy' }}</mat-icon>
                        {{ payLinkCopied ? 'Link copied' : 'Copy payment link' }}
                      </button>
                    }
                  }
                  @if (canCheckPayment && (order.orderStatus === 'PENDING' || order.orderStatus === 'PENDING COLLECTION' || order.orderStatus === 'CANCELLED')) {
                    <button class="flex items-center gap-2 px-3 py-2.5 rounded-xl border border-gray-200 hover:border-amber-300 hover:bg-amber-50/50 font-semibold text-gray-700 transition-colors text-left disabled:opacity-50"
                      [disabled]="checkPaymentProgress" (click)="onCheckPayment()">
                      <mat-icon class="!text-[18px] !h-[18px] !w-[18px] !text-amber-500">verified</mat-icon> Check payment
                      @if (checkPaymentProgress) {
                        <span class="ml-auto"><mat-spinner diameter="16"></mat-spinner></span>
                      }
                    </button>
                  }
                  @if (order.orderStatus === 'PENDING' || order.orderStatus === 'PENDING COLLECTION') {
                    <button class="flex items-center gap-2 px-3 py-2.5 rounded-xl border border-red-200 hover:bg-red-50 font-semibold text-red-600 transition-colors text-left"
                      (click)="onCancelClick()">
                      <mat-icon class="!text-[18px] !h-[18px] !w-[18px]">cancel</mat-icon> Cancel order
                    </button>
                  }

                  <div class="mt-2 pt-3 border-t border-gray-100">
                    <div class="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Order status</div>
                    @if (order.orderStatus && orderStatusToAvoidUpdate.includes(order.orderStatus)) {
                      <span class="inline-block text-xs font-semibold px-3 py-1 rounded-full capitalize" [ngClass]="statusPill(order.orderStatus)">{{ order.orderStatus | lowercase }}</span>
                    } @else {
                      <select class="w-full px-3 py-2 rounded-xl border border-gray-200 bg-gray-50 text-sm font-medium" [(ngModel)]="order.orderStatus" (ngModelChange)="onStatusUpdate()">
                        <option [value]="'PENDING'">PENDING</option>
                        <option [value]="'PENDING COLLECTION'">PENDING COLLECTION</option>
                        <option [value]="'ON HOLD'">ON HOLD</option>
                        <option [value]="'PROCESSING'">PROCESSING</option>
                      </select>
                    }
                  </div>
                </div>
              </div>
            </div>

            <!-- Products -->
            <div class="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              <div class="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-b border-gray-100">
                <div class="flex items-center gap-2">
                  <mat-icon class="!text-[20px] !h-5 !w-5 !text-amber-500">inventory_2</mat-icon>
                  <h2 class="font-semibold text-gray-800">Products</h2>
                  <span class="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{{ productsForCart.length }}</span>
                </div>
                <div class="flex items-center gap-2">
                  @if (!modifyProducts && editProductVisible && (order.orderStatus === 'PENDING' || order.orderStatus === 'ON HOLD' || order.orderStatus === 'PENDING COLLECTION')) {
                    <button class="flex items-center gap-1 px-4 py-2 rounded-xl border border-amber-300 text-amber-600 font-semibold text-xs hover:bg-amber-400 hover:text-neutral-900 hover:border-amber-400 transition-all"
                      (click)="onModifyProducts()">
                      <mat-icon class="!text-[16px] !h-4 !w-4">edit</mat-icon> Edit products
                    </button>
                  }
                  @if (modifyProducts) {
                    <button class="px-4 py-2 rounded-xl bg-amber-400 text-neutral-900 font-semibold text-xs hover:bg-amber-300" (click)="saveOrder(true,false)">Save changes</button>
                  }
                  @if (modifyProducts) {
                    <button class="px-4 py-2 rounded-xl bg-gray-100 text-gray-600 font-semibold text-xs hover:bg-gray-200" (click)="onCancelChanges()">Cancel</button>
                  }
                </div>
              </div>
              <div class="px-5 pb-5">
                @if (modifyProducts) {
                  <div class="mt-3">
                    <div class="w-full">
                      @if(productSearchLoading) {
                        <p class="text-xs animate-pulse text-yellow-500">Product search loading ..</p>
                        }@else {
                        <app-typehead
                          [dataSet]="productSuggestionList"
                          [keysToMatch]="['name','sku']"
                          (onModelChangedEvent)="onProductSelection($event)"
                          [resetEachSelect]="true"
                        ></app-typehead>
                      }
                    </div>
                    <!-- Quantity selection -->
                    <div class="w-full">
                      @if( editingProduct ) {
                        <div class="mt-5 shadow-lg rounded-sm p-4 bg-slate-100">
                          <!-- prod title -->
                          <div class="text-lg font-bold underline cursor-pointer">
                            <a
                              href="{{ frontEndURL + editingProduct.product.productSlug }}"
                              target="_blank"
                              >
                              {{ editingProduct.product.productName }}
                            </a>
                          </div>
                        </div>
                        <!-- Template 1 with with formats  -->
                        @if( editingProduct.product.productTemplateId === 1 ) {
                          <div class="mt-5">
                            @for (variant of editingProduct.variants; track $index) {
                              <!-- Need to avoid the first varient -->
                              @if (editingProduct.variants.length > 0) {
                                <div
                                  class="p-4 border-2 grid grid-cols-4 gap-x-6"
                    [ngClass]="{
                      'bg-slate-50': $index % 2 === 0
                    }"
                                  >
                                  <div class="flex flex-col gap-2 ">
                                    <span class="font-bold">{{ variant.format }}</span>
                                    <span>{{ variant?.deliveryterm?.deliveryTermDesc }}</span>
                                    <span>Weight: {{ variant.weight }} Kg</span>
                                  </div>
                                  <div>
                                    <div class="flex flex-row justify-between font-bold">
                                      <span>Price:</span>
                                      <span>£{{ variant.price }}</span>
                                    </div>
                                    @if (variant.discPrice > 0) {
                                      <div class="flex flex-row justify-between text-green-700">
                                        <span>Discount Price:</span>
                                        <span>£{{ variant.discPrice }}</span>
                                      </div>
                                    }
                                  </div>
                                  <div>
                                    <div class="flex flex-row justify-between text-green-500">
                                      <span>Markstey Stock:</span>
                                      <span>{{ variant.mStockQty }}</span>
                                    </div>
                                    <!-- <div class="flex flex-row justify-between text-amber-600">
                                    <span>Kettering Stock:</span>
                                    <span>{{ variant.kStockQty }}</span>
                                  </div> -->
                                  <div class="flex flex-row justify-between font-bold">
                                    <span>Total Stock:</span>
                                    <span>{{ variant.stockQty }}</span>
                                  </div>
                                </div>
                                <div class="flex flex-col gap-y-2">
                                  <div>
                                    <label>Quantity:</label>
                                    <div>
                                      <input
                                        type="number"
                                        class="p-2 rounded shodow-sm bg-white w-full"
                                        [(ngModel)]="variant.quantity"
                                        [disabled]="variant.stockQty <= 0"
                                        min="0"
                                        />
                                    </div>
                                  </div>
                                  <div>
                                    <button
                                      mat-raised-button
                                      color="primary"
                                      (click)="onAddToCart(variant)"
                                      [disabled]="variant.stockQty <= 0"
                                      >
                                      <span class="bg-yellow-300 rounded px-2 py-1 inline-block">
                                        Add to cart
                                      </span>
                                    </button>
                                  </div>
                                </div>
                              </div>
                              } }
                            </div>
                          }
                          <!-- Template with with formats  -->
                          <!-- Template 2 with slabs and crates -->
                          @if( editingProduct.product.productTemplateId === 2 && slabsCrates ) {
                            <div class="mt-5">
                              <div class="bg-slate-400 p-4 border-2 my-4">
                                <label>Area Coverage:</label>
                                <div>
                                  <input
                                    type="number"
                                    class="p-2 rounded shodow-sm bg-white w-full"
                                    [(ngModel)]="areaCoverage"
                                    (ngModelChange)="onAreaChange()"
                                    />
                                </div>
                              </div>
                              <div class="p-4 border-2 grid grid-cols-4 gap-x-6">
                                <div class="flex flex-col gap-2">
                                  <span class="font-bold">{{ slabsCrates.slab.format }}</span>
                                  <span>{{ slabsCrates.slab?.deliveryterm?.deliveryTermDesc }}</span>
                                  <span>Weight: {{ slabsCrates.slab.weight }} Kg</span>
                                </div>
                                <div>
                                  <div class="flex flex-row justify-between font-bold">
                                    <span>Price:</span>
                                    <span>£{{ slabsCrates.slab.price }}</span>
                                  </div>
                                  @if (slabsCrates.slab.discPrice > 0) {
                                    <div class="flex flex-row justify-between text-green-700">
                                      <span>Discount Price:</span>
                                      <span>£{{ slabsCrates.slab.discPrice }}</span>
                                    </div>
                                  }
                                </div>
                                <div>
                                  <div class="flex flex-row justify-between text-green-500">
                                    <span>Markstey Stock:</span>
                                    <span>{{ slabsCrates.slab.mStockQty }}</span>
                                  </div>
                                  <!-- <div class="flex flex-row justify-between text-amber-600">
                                  <span>Kettering Stock:</span>
                                  <span>{{ slabsCrates.slab.kStockQty }}</span>
                                </div> -->
                                <div class="flex flex-row justify-between font-bold">
                                  <span>Total Stock:</span>
                                  <span>{{ slabsCrates.slab.stockQty }}</span>
                                </div>
                              </div>
                              <div class="flex flex-col gap-y-2">
                                <div>
                                  <label>Quantity:</label>
                                  <div>
                                    <input
                                      type="number"
                                      class="p-2 rounded shodow-sm bg-white w-full"
                                      [(ngModel)]="slabsCrates.slab.quantity"
                                      (ngModelChange)="onProductQuantityChange()"
                                      [disabled]="slabsCrates.slab.stockQty <= 0"
                                      min="0"
                                      />
                                  </div>
                                </div>
                                <div>
                                  <button
                                    mat-raised-button
                                    color="primary"
                                    (click)="onAddToCart(slabsCrates.slab)"
                                    [disabled]="slabsCrates.slab.stockQty <= 0"
                                    >
                                    <span class="bg-yellow-300 rounded px-2 py-1 inline-block">
                                      Add to cart
                                    </span>
                                  </button>
                                </div>
                              </div>
                            </div>
                            <div class="p-4 border-2 grid grid-cols-4 gap-x-6">
                              <div class="flex flex-col gap-2">
                                <span class="font-bold">{{ slabsCrates.crate.format }}</span>
                                <span>{{ slabsCrates.crate?.deliveryterm?.deliveryTermDesc }}</span>
                                <span>Weight: {{ slabsCrates.crate.weight }} Kg</span>
                              </div>
                              <div>
                                <div class="flex flex-row justify-between font-bold">
                                  <span>Price:</span>
                                  <span>£{{ slabsCrates.crate.price }}</span>
                                </div>
                                @if (slabsCrates.crate.discPrice > 0) {
                                  <div class="flex flex-row justify-between text-green-700">
                                    <span>Discount Price:</span>
                                    <span>£{{ slabsCrates.crate.discPrice }}</span>
                                  </div>
                                }
                              </div>
                              <div>
                                <div class="flex flex-row justify-between text-green-500">
                                  <span>Markstey Stock:</span>
                                  <span>{{ slabsCrates.crate.mStockQty }}</span>
                                </div>
                                <!-- <div class="flex flex-row justify-between text-amber-600">
                                <span>Kettering Stock:</span>
                                <span>{{ slabsCrates.crate.kStockQty }}</span>
                              </div> -->
                              <div class="flex flex-row justify-between font-bold">
                                <span>Total Stock:</span>
                                <span>{{ slabsCrates.crate.stockQty }}</span>
                              </div>
                            </div>
                            <div class="flex flex-col gap-y-2">
                              <div>
                                <label>Quantity:</label>
                                <div>
                                  <input
                                    type="number"
                                    class="p-2 rounded shodow-sm bg-white w-full"
                                    [(ngModel)]="slabsCrates.crate.quantity"
                                    (ngModelChange)="onProductQuantityChange()"
                                    [disabled]="slabsCrates.crate.stockQty <= 0"
                                    min = "0"
                                    />
                                </div>
                              </div>
                              <div>
                                <button
                                  mat-raised-button
                                  color="primary"
                                  (click)="onAddToCart(slabsCrates.crate)"
                                  [disabled]="slabsCrates.crate.stockQty <= 0"
                                  >
                                  <span class="bg-yellow-300 rounded px-2 py-1 inline-block">
                                    Add to cart
                                  </span>
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      }
                      <!-- Template with slabs and crates -->
                    }
                  </div>
                  <!-- Quantity selection -->
                </div>
              }

              <div class="overflow-x-auto mt-4 rounded-xl border border-gray-100">
                <table class="w-full text-sm">
                  <thead>
                    <tr class="text-left text-xs font-semibold text-gray-400 uppercase tracking-wide bg-gray-50 whitespace-nowrap">
                      <th class="px-4 py-3">Product</th>
                      <th class="px-3 py-3 text-right">Weight</th>
                      <th class="px-3 py-3 text-right">Unit price</th>
                      <th class="px-3 py-3 text-center">Qty</th>
                      <th class="px-3 py-3 text-right">Total</th>
                      <th class="px-3 py-3 text-right">VAT</th>
                      <th class="px-3 py-3 text-right">Net total</th>
                      <th class="px-3 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    @for (orderItem of productsForCart; track $index) {
                      <tr class="border-t border-gray-50 hover:bg-amber-50/30 transition-colors">
                        <td class="px-4 py-4 min-w-[280px]">
                          <div class="font-semibold text-gray-900 leading-snug">{{ orderItem.name }}</div>
                          <div class="mt-1 flex flex-wrap items-center gap-1.5">
                            <span class="text-[11px] text-gray-400">SKU {{ orderItem.sku }}</span>
                            @if (orderItem.productFormat) {
                              <span class="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{{ orderItem.productFormat }}</span>
                            }
                            @if (orderItem.store) {
                              <span class="flex items-center gap-0.5 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-sky-50 text-sky-700">
                                <mat-icon class="!text-[12px] !h-3 !w-3">warehouse</mat-icon>{{ orderItem.store }}
                              </span>
                            }
                            @if (orderItem.deliveryTermSlug) {
                              <span class="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">{{ orderItem.deliveryTermSlug }}</span>
                            }
                          </div>
                        </td>
                        <td class="px-3 py-4 text-right text-gray-600 whitespace-nowrap">{{ (orderItem.weight * orderItem.quantity) | number:'1.0-2' }} kg</td>
                        <td class="px-3 py-4 text-right text-gray-700 whitespace-nowrap">£{{ orderItem.unitPrice | number:'1.2-2' }}</td>
                        <td class="px-3 py-4 text-center whitespace-nowrap">
                          <div class="inline-flex items-center gap-1.5">
                            @if (modifyProducts) {
                              <button class="h-7 w-7 rounded-full bg-amber-300 hover:bg-amber-400 font-bold text-neutral-900 leading-none"
                              (click)="onQuantityChange($index, 'decrease')">-</button>
                            }
                            <span class="min-w-[36px] text-center font-bold text-gray-900 px-2.5 py-1 rounded-full bg-gray-100">{{ orderItem.quantity }}</span>
                            @if (modifyProducts) {
                              <button class="h-7 w-7 rounded-full bg-amber-300 hover:bg-amber-400 font-bold text-neutral-900 leading-none"
                              (click)="onQuantityChange($index,'increase')">+</button>
                            }
                          </div>
                        </td>
                        <td class="px-3 py-4 text-right text-gray-600 whitespace-nowrap">£{{ (orderItem.unitPrice * orderItem.quantity  * 5 / 6) | number:'1.2-2' }}</td>
                        <td class="px-3 py-4 text-right text-gray-600 whitespace-nowrap">£{{ (orderItem.unitPrice * orderItem.quantity * 1 / 6) | number:'1.2-2' }}</td>
                        <td class="px-3 py-4 text-right font-bold text-gray-900 whitespace-nowrap">£{{ (orderItem.unitPrice * orderItem.quantity) | number:'1.2-2' }}</td>
                        <td class="px-3 py-4 text-right">
                          @if (modifyProducts) {
                            <button (click)="onCartItemRemoval($index)" class="h-7 w-7 rounded-full bg-red-50 hover:bg-red-100 text-red-600 flex items-center justify-center ml-auto" matTooltip="Remove">
                              <mat-icon class="!text-[16px] !h-4 !w-4">close</mat-icon>
                            </button>
                          }
                        </td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>

              <!-- Totals -->
              <div class="mt-4 flex flex-col-reverse sm:flex-row sm:items-start justify-between gap-4">
                <div class="flex flex-wrap gap-3">
                  <div class="rounded-xl border border-gray-100 bg-gray-50 px-4 py-3 min-w-[110px]">
                    <div class="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">Lines</div>
                    <div class="text-lg font-bold text-gray-900">{{ productsForCart.length }}</div>
                  </div>
                  <div class="rounded-xl border border-gray-100 bg-gray-50 px-4 py-3 min-w-[110px]">
                    <div class="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">Units</div>
                    <div class="text-lg font-bold text-gray-900">{{ totalUnits }}</div>
                  </div>
                  <div class="rounded-xl border border-gray-100 bg-gray-50 px-4 py-3 min-w-[110px]">
                    <div class="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">Total weight</div>
                    <div class="text-lg font-bold text-gray-900">{{ totalWeightKg | number:'1.0-2' }} kg</div>
                  </div>
                </div>
                <div class="w-full sm:w-96 rounded-xl bg-gray-50 border border-gray-100 p-4 text-sm flex flex-col gap-2">
                  <div class="flex justify-between">
                    <span class="text-gray-500">Subtotal</span>
                    <span class="font-semibold text-gray-800">£{{ modifyProducts || modifyCustomer || modifyDelivery ? subTotal.toFixed(2) : order.orderAmount }}</span>
                  </div>
                  @if (handleCharge > 0 || order.handleCharge > 0) {
                    <div class="flex justify-between">
                      <span class="text-gray-500">Handling fee</span>
                      <span class="font-semibold text-gray-800">£{{ modifyProducts || modifyCustomer || modifyDelivery ? handleCharge.toFixed(2) : order.handleCharge }}</span>
                    </div>
                  }
                  @if (shipCost > 0 || order.shipCost > 0) {
                    <div class="flex justify-between">
                      <span class="text-gray-500">Shipping <span class="text-xs">via {{deliveryType}}</span></span>
                      <span class="font-semibold text-gray-800">£{{ modifyProducts || modifyCustomer || modifyDelivery ? shipCost.toFixed(2) : order.shipCost }}</span>
                    </div>
                  }
                  @if (totalDiscountedAmount > 0 || order.discAmount > 0) {
                    <div class="flex justify-between text-emerald-700">
                      <span>{{ isVatDiscounted ? 'VAT amount' : 'Discount' }}</span>
                      <span class="font-semibold">-£{{ modifyProducts || modifyCustomer || modifyDelivery ? totalDiscountedAmount.toFixed(2) : order.discAmount }}</span>
                    </div>
                  }
                  <div class="flex justify-between items-baseline pt-2 mt-1 border-t border-gray-200">
                    <span class="font-semibold text-gray-800">Net total</span>
                    <span class="text-2xl font-bold text-gray-900">£{{ modifyProducts || modifyCustomer || modifyDelivery ? netTotal.toFixed(2) : order.netAmount }}</span>
                  </div>
                  @if (((modifyProducts || modifyCustomer || modifyDelivery) && netTotal > 0) || (!modifyProducts && !modifyCustomer && !modifyDelivery && order.netAmount > 0)) {
                    @if (!isVatDiscounted) {
                      <div class="text-xs text-gray-400 text-right">
                        includes £{{ modifyProducts || modifyCustomer || modifyDelivery ? (subTotal / 6).toFixed(2) : (order.orderAmount / 6).toFixed(2) }} VAT
                      </div>
                    }
                  }
                  @if (totalRefundedAmount > 0) {
                    <div class="flex justify-between items-baseline pt-2 mt-1 border-t border-amber-200 bg-amber-50 -mx-4 px-4 py-2">
                      <span class="font-semibold text-amber-800">Net balance</span>
                      <span class="text-xl font-bold text-amber-900">£{{ (order.netAmount - totalRefundedAmount) | number:'1.2-2' }}</span>
                    </div>
                    <div class="text-xs text-gray-500 text-right">£{{ totalRefundedAmount | number:'1.2-2' }} refunded</div>
                  }
                </div>
              </div>
            </div>
          </div>

          <!-- Customer edit (search / guest) -->
          @if (modifyCustomer) {
            <div class="flex flex-col gap-4">
              @if (modifyCustomer) {
                <div>
                  <!-- Customer Selection -->
                  <div class="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm" >
                    <div class="flex justify-between">
                      <div>
                        <input
                          type="radio"
                          name="customerMode"
                          [checked]="customerMode === 'CUSTOMER'"
                          (click)="onCustomerModeSelection('CUSTOMER')"
                          />
                        <label class="ml-2 text-xl mt-2">Select Customer:</label>
                      </div>
                      <div class="mt-2">
                        Not Found?
                        <button
                          class="underline text-green-600 font-semibold"
                          (click)="onCreateCustomer()"
                          >
                          Create New
                        </button>
                      </div>
                    </div>
                    @if (customerMode === 'CUSTOMER') {
                      <!-- search box -->
                      <div class="mt-3 flex flex-row justify-center">
                        <div class="w-full">
                          <app-customer-typehead
                            [searchFn]="searchCustomers"
                            (onModelChangedEvent)="onCustomerSelection($event)"
                          ></app-customer-typehead>
                        </div>
                      </div>
                      <!-- search box -->
                      <!-- Customer Search -->
                      <!-- Selected Customer Summary -->
                      @if(selectedCustomer) {
                        <div class="mt-5 shadow-lg rounded-sm p-4 bg-slate-100">
                          <div class="flex flex-col">
                            <!-- Basic customer details -->
                            <div class="flex flex-row">
                              @if (selectedCustomer) {
                                <div class="p-4 border flex flex-row justify-between">
                                  <span class="font-bold">Customer:</span>
                                  <span class="ml-4">{{ selectedCustomer.firstname + ' ' +  selectedCustomer.lastname }}</span>
                                </div>
                              }
                              @if (selectedCustomer.useremail) {
                                <div class="p-4 border flex flex-row justify-between">
                                  <span class="font-bold">Email:</span>
                                  <span class="ml-4">{{ selectedCustomer.useremail }}</span>
                                </div>
                                } @if (selectedCustomer.phone) {
                                <div class="p-4 border flex flex-row justify-between">
                                  <span class="font-bold">Phone no:</span>
                                  <span class="ml-4">{{ selectedCustomer.phone }}</span>
                                </div>
                              }
                            </div>
                            <!-- Basic customer details -->
                            <!-- Customer addresses -->
                            <div class="mt-4">
                              <label class="text-xl mt-2">Customer's addresses</label>
                              <div class="mt-4">
                                <!-- Billing Addresses -->
                                @if (billingAddresses.length > 0) {
                                  <mat-accordion>
                                    <mat-expansion-panel
                                      (opened)="panelOpenState.set(true)"
                                      (closed)="panelOpenState.set(false)"
                                      >
                                      <mat-expansion-panel-header>
                                        <mat-panel-title>
                                          Billing Addresses ({{ billingAddresses.length }})
                                        </mat-panel-title>
                                      </mat-expansion-panel-header>
                                      <div>
                                        @for (address of billingAddresses; track $index) {
                                          <div
                                            class="mt-2 flex flex-row justify-between items-center border p-2 shadow-sm rounded-md"
                                            >
                                            <span class="basis-1/12">
                                              <input
                                                type="radio"
                                                name="addressSelectionBilling"
                                                (click)="onAddressSelection(address)"
                                                [checked]="address.isDefault"
                                                />
                                            </span>
                                            <div class="basis-10/12 text-sm">
                                              @if(address.isDefault) {
                                                <div>
                                                  Customer marked as default
                                                  {{ address.addressType }} address
                                                </div>
                                              }
                                              <div class="font-semibold">
                                                {{ address.firstName + " " + address.lastName }}
                                              </div>
                                              <div>
                                                {{ address.address1 + ", " }}
                                                @if ( address.address2) {
                                                  {{ address.address2 + ", " }}
                                                }
                                                {{
                                                address.city +
                                                ", " +
                                                address.postCode +
                                                ", " +
                                                address.country
                                                }}
                                              </div>
                                              <div>{{ address.phoneNo }}</div>
                                            </div>
                                            <span class="basis-1/12">
                                              <button (click)="onCreateNewAddress(address)">
                                                <i class="fa fa-pencil-square" aria-hidden="true"></i>
                                              </button>
                                            </span>
                                          </div>
                                        }
                                      </div>
                                    </mat-expansion-panel>
                                  </mat-accordion>
                                }
                                <!-- Billing Addresses -->
                                <!-- No billing address found -->
                                @if (billingAddresses.length === 0) {
                                  <div
                                    class="mt-2 flex flex-row justify-center items-center border p-4 shadow-sm rounded-md"
                                    >
                                    No billing addresses found!
                                  </div>
                                }
                                <!-- No billing address found -->
                                <!-- Same address checkbox -->
                                <div class="mt-4">
                                  <input
                                    type="checkbox"
                                    name="sameAddressCheckbox"
                                    [(ngModel)]="sameAddress"
                                    (change)="onSamaddressChange()"
                                    />
                                  Shipping address is same as billing address
                                </div>
                                <!-- Same address checkbox -->
                                <!-- Shipping Addresses -->
                                @if (shippingAddresses.length > 0 && !sameAddress) {
                                  <div class="mt-2">
                                    <mat-accordion>
                                      <mat-expansion-panel
                                        (opened)="panelOpenState.set(true)"
                                        (closed)="panelOpenState.set(false)"
                                        >
                                        <mat-expansion-panel-header>
                                          <mat-panel-title>
                                            Shipping Addresses ({{ shippingAddresses.length }})
                                          </mat-panel-title>
                                        </mat-expansion-panel-header>
                                        <div>
                                          @for (address of shippingAddresses; track $index) {
                                            <div
                                              class="mt-2 flex flex-row justify-between items-center border p-2 shadow-sm rounded-md"
                                              >
                                              <span class="basis-1/12">
                                                <input
                                                  type="radio"
                                                  name="addressSelectionShipping"
                                                  (click)="onAddressSelection(address)"
                                                  [checked]="address.isDefault"
                                                  />
                                              </span>
                                              <div class="basis-10/12 text-sm">
                                                @if(address.isDefault) {
                                                  <div>
                                                    Customer marked as default
                                                    {{ address.addressType }} address
                                                  </div>
                                                }
                                                <div class="font-semibold">
                                                  {{ address.firstName + " " + address.lastName }}
                                                </div>
                                                <div>
                                                  {{ address.address1 + ", " }}
                                                  @if ( address.address2) {
                                                    {{ address.address2 + ", " }}
                                                  }
                                                  {{
                                                  address.city +
                                                  ", " +
                                                  address.postCode +
                                                  ", " +
                                                  address.country
                                                  }}
                                                </div>
                                                <div>{{ address.phoneNo }}</div>
                                              </div>
                                              <span class="basis-1/12">
                                                <button (click)="onCreateNewAddress(address)">
                                                  <i class="fa fa-pencil-square" aria-hidden="true"></i>
                                                </button>
                                              </span>
                                            </div>
                                          }
                                        </div>
                                      </mat-expansion-panel>
                                    </mat-accordion>
                                  </div>
                                }
                                <!-- Shipping Addresses -->
                                <!-- No shipping address found -->
                                @if (shippingAddresses.length === 0  && !sameAddress) {
                                  <div
                                    class="mt-2 flex flex-row justify-center items-center border p-4 shadow-sm rounded-md"
                                    >
                                    No shipping addresses found!
                                  </div>
                                }
                                <!-- No shipping address found -->
                                <!-- Manage Address Link -->
                                <div class="mt-4 underline text-amber-600 font-semibold text-sm cursor-pointer" (click)="onCreateNewAddress()">
                                  Create new shipping / billing Addresses
                                </div>
                                <!-- Manage Address Link -->
                              </div>
                            </div>
                            <!-- Customer addresses -->
                          </div>
                        </div>
                      }
                      <!-- Selected Customer Summary -->
                    }
                  </div>
                  <!-- Customer Selection -->
                </div>
              }
              @if (modifyCustomer) {
                <div class="mt-4">
                  <div class="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                    <input
                      type="radio"
                      name="customerMode"
                      [checked]="customerMode === 'GUEST'"
                      (click)="onCustomerModeSelection('GUEST')"
                      />
                    <label class="ml-2 text-xl mt-2">Guest Mode</label>
                    <!-- Customer Billing Details -->
                    @if (customerMode === 'GUEST') {
                      <div class="mt-5 shadow-lg rounded-sm p-4 bg-slate-100">
                        <app-billing-form
                          [defaultBillingAddress]="billingAddress"
                          [defaultShippingAddress]="shippingAdress"
                          (formValid)="onBillingFormSubmit($event)"
                        ></app-billing-form>
                      </div>
                    }
                    <!-- Customer Billing Details -->
                  </div>
                </div>
              }
            </div>
          }

          <!-- Customer -->
          <div class="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div class="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-b border-gray-100">
              <div class="flex items-center gap-2">
                <mat-icon class="!text-[20px] !h-5 !w-5 !text-amber-500">person</mat-icon>
                <h2 class="font-semibold text-gray-800">Customer</h2>
              </div>
              <div class="flex items-center gap-2">
                @if (!modifyCustomer && editCustVisible && (order.orderStatus === 'PENDING' || order.orderStatus === 'ON HOLD' || order.orderStatus === 'PENDING COLLECTION')) {
                  <button class="flex items-center gap-1 px-4 py-2 rounded-xl border border-amber-300 text-amber-600 font-semibold text-xs hover:bg-amber-400 hover:text-neutral-900 hover:border-amber-400 transition-all"
                    (click)="onModifyCustomer()">
                    <mat-icon class="!text-[16px] !h-4 !w-4">edit</mat-icon> Edit customer
                  </button>
                }
                @if (modifyCustomer) {
                  <button class="px-4 py-2 rounded-xl bg-amber-400 text-neutral-900 font-semibold text-xs hover:bg-amber-300" (click)="saveOrder(false,false)">Save changes</button>
                }
                @if (modifyCustomer) {
                  <button class="px-4 py-2 rounded-xl bg-gray-100 text-gray-600 font-semibold text-xs hover:bg-gray-200" (click)="onCancelCustomerChanges()">Cancel</button>
                }
              </div>
            </div>
            <div class="p-5 flex flex-col gap-4">

              @if (billingAddress) {
                <!-- Who -->
                <div class="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  <div class="flex items-center gap-4">
                    <div class="h-14 w-14 rounded-full bg-neutral-900 text-amber-400 text-lg font-bold flex items-center justify-center flex-shrink-0 ring-4 ring-amber-100">
                      {{ (billingAddress.firstName || '?')[0] | uppercase }}{{ (billingAddress.lastName || '')[0] | uppercase }}
                    </div>
                    <div>
                      <div class="flex flex-wrap items-center gap-2">
                        <div class="font-bold text-gray-900 text-xl leading-tight">{{ billingAddress.firstName + " " + billingAddress.lastName }}</div>
                        @if (order.custType) {
                          <span class="text-[11px] font-semibold px-2.5 py-0.5 rounded-full bg-amber-100 text-amber-800">{{ order.custType | titlecase }}</span>
                        }
                        @if (!order.customerId) {
                          <span class="text-[11px] font-semibold px-2.5 py-0.5 rounded-full bg-gray-100 text-gray-600">Guest checkout</span>
                        }
                      </div>

                      <!-- Contact: phone + email (email edit stays here) -->
                      <div class="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600">
                        @if (billingAddress.phoneNo) {
                          <span class="inline-flex items-center gap-1.5">
                            <mat-icon class="!text-[16px] !h-4 !w-4 !text-gray-400">call</mat-icon>
                            <a class="hover:text-gray-900" [href]="'tel:' + billingAddress.phoneNo">{{ billingAddress.phoneNo }}</a>
                            <button class="h-6 w-6 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-700 flex items-center justify-center" (click)="copyText(billingAddress.phoneNo, 'Phone number')" matTooltip="Copy phone number">
                              <mat-icon class="!text-[14px] !h-[14px] !w-[14px]">content_copy</mat-icon>
                            </button>
                          </span>
                        }
                        <span class="inline-flex items-center gap-1.5">
                          <mat-icon class="!text-[16px] !h-4 !w-4 !text-gray-400">mail</mat-icon>
                          @if (!modifyEmail) {
                            <a class="hover:text-gray-900 break-all" [href]="'mailto:' + billingAddress.email">{{ billingAddress.email }}</a>
                            <button class="h-6 w-6 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-700 flex items-center justify-center" (click)="copyText(billingAddress.email, 'Email')" matTooltip="Copy email">
                              <mat-icon class="!text-[14px] !h-[14px] !w-[14px]">content_copy</mat-icon>
                            </button>
                            @if ((this.userInfo.userName == 'Nikhil5 Bhaskar' || this.userInfo.userName == 'Santosh Singh Dhami' || this.userInfo.userName == 'Debanjan techclouds')) {
                              <button class="text-xs font-semibold text-amber-600 hover:text-amber-800 underline" (click)="onModifyEmail()"
                              >Edit</button>
                            }
                          } @else {
                            <input class="px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-sm w-64 focus:outline-none focus:ring-2 focus:ring-amber-400" [(ngModel)]="customerEmail" />
                            <button class="px-3 py-1.5 rounded-lg bg-amber-400 text-neutral-900 text-xs font-semibold hover:bg-amber-300" (click)="updateEmail()">Save</button>
                            <button class="px-3 py-1.5 rounded-lg bg-gray-100 text-gray-600 text-xs font-semibold hover:bg-gray-200" (click)="onCancelEmailChanges()">Cancel</button>
                          }
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              }

              <div class="grid grid-cols-1 lg:grid-cols-3 gap-4">

                @if (billingAddress) {
                  <div class="rounded-xl border border-gray-100 bg-gray-50/50 p-4 flex flex-col">
                    <div class="flex items-center justify-between gap-2 mb-3">
                      <div class="flex items-center gap-2">
                        <span class="h-8 w-8 rounded-lg bg-white border border-gray-100 flex items-center justify-center">
                          <mat-icon class="!text-[18px] !h-[18px] !w-[18px] !text-amber-500">receipt_long</mat-icon>
                        </span>
                        <h3 class="font-semibold text-gray-800 text-sm">Billing address</h3>
                      </div>
                    </div>

                    @if (!modifyBillingAddress) {
                      <div class="text-sm text-gray-600 leading-relaxed flex-1">
                        <div class="font-medium text-gray-900">{{ billingAddress.address1 }}</div>
                        @if (billingAddress.address2) {
                          <div>{{ billingAddress.address2 }}</div>
                        }
                        <div>{{ billingAddress.city }}</div>
                        <div class="mt-2 flex items-center gap-2">
                          <span class="text-sm font-bold tracking-wide px-2.5 py-0.5 rounded-md bg-neutral-900 text-amber-400">{{ billingAddress.postCode }}</span>
                          <span class="text-xs text-gray-400">{{ billingAddress.country }}</span>
                        </div>
                        @if (billingAddress.phoneNo) {
                          <a class="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-gray-900" [href]="'tel:' + billingAddress.phoneNo">
                            <mat-icon class="!text-[14px] !h-[14px] !w-[14px]">call</mat-icon>{{ billingAddress.phoneNo }}
                          </a>
                        }
                      </div>
                      <div class="mt-3 pt-3 border-t border-gray-100 flex items-center gap-1">
                        <button class="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-gray-500 hover:bg-white hover:text-gray-900 transition-colors" (click)="copyText(addressText(billingAddress), 'Address')" matTooltip="Copy the address">
                          <mat-icon class="!text-[15px] !h-[15px] !w-[15px]">content_copy</mat-icon> Copy
                        </button>
                        <a class="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-gray-500 hover:bg-white hover:text-gray-900 transition-colors" [href]="mapsUrl(billingAddress)" target="_blank" rel="noopener" matTooltip="Open in Google Maps">
                          <mat-icon class="!text-[15px] !h-[15px] !w-[15px]">map</mat-icon> Map
                        </a>
                        @if ((this.userInfo.userName == 'Nikhil5 Bhaskar' || this.userInfo.userName == 'Santosh Singh Dhami' || this.userInfo.userName == 'Debanjan techclouds')) {
                          <button class="ml-auto flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-amber-600 hover:bg-amber-50 transition-colors" (click)="onModifyBillingAddress()"
                            >
                            <mat-icon class="!text-[15px] !h-[15px] !w-[15px]">edit</mat-icon> Edit
                          </button>
                        }
                      </div>
                    } @else {
                      <div class="flex flex-col gap-1.5">
                        <input class="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" [(ngModel)]="editAddress1" placeholder="Address line 1" />
                        <input class="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" [(ngModel)]="editAddress2" placeholder="Address line 2" />
                        <input class="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" [(ngModel)]="editCity" placeholder="City" />
                        <input class="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" [(ngModel)]="editCountry" placeholder="Country" />
                        <input class="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" [(ngModel)]="editPostCode" placeholder="Post code" />
                        <input class="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" [(ngModel)]="editPhoneNo" placeholder="Phone" />
                      </div>
                      <div class="mt-3 flex items-center gap-2">
                        <button class="px-4 py-2 rounded-lg bg-amber-400 text-neutral-900 text-xs font-semibold hover:bg-amber-300" (click)="updateBillingAddress()">Save address</button>
                        <button class="px-4 py-2 rounded-lg bg-gray-100 text-gray-600 text-xs font-semibold hover:bg-gray-200" (click)="onCancelBillingAddressChanges()">Cancel</button>
                      </div>
                    }
                  </div>
                }


                @if (shippingAdress) {
                  <div class="rounded-xl border border-gray-100 bg-gray-50/50 p-4 flex flex-col">
                    <div class="flex items-center justify-between gap-2 mb-3">
                      <div class="flex items-center gap-2">
                        <span class="h-8 w-8 rounded-lg bg-white border border-gray-100 flex items-center justify-center">
                          <mat-icon class="!text-[18px] !h-[18px] !w-[18px] !text-amber-500">local_shipping</mat-icon>
                        </span>
                        <h3 class="font-semibold text-gray-800 text-sm">Delivery address</h3>
                      </div>
                      @if (deliverySameAsBilling) {
                        <span class="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-sky-50 text-sky-700">Same as billing</span>
                      }
                    </div>

                    @if (!modifyShippingAddress) {
                      <div class="text-sm text-gray-600 leading-relaxed flex-1">
                        <div class="font-medium text-gray-900">{{ shippingAdress.address1 }}</div>
                        @if (shippingAdress.address2) {
                          <div>{{ shippingAdress.address2 }}</div>
                        }
                        <div>{{ shippingAdress.city }}</div>
                        <div class="mt-2 flex items-center gap-2">
                          <span class="text-sm font-bold tracking-wide px-2.5 py-0.5 rounded-md bg-neutral-900 text-amber-400">{{ shippingAdress.postCode }}</span>
                          <span class="text-xs text-gray-400">{{ shippingAdress.country }}</span>
                        </div>
                        @if (shippingAdress.phoneNo) {
                          <a class="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-gray-900" [href]="'tel:' + shippingAdress.phoneNo">
                            <mat-icon class="!text-[14px] !h-[14px] !w-[14px]">call</mat-icon>{{ shippingAdress.phoneNo }}
                          </a>
                        }
                      </div>
                      <div class="mt-3 pt-3 border-t border-gray-100 flex items-center gap-1">
                        <button class="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-gray-500 hover:bg-white hover:text-gray-900 transition-colors" (click)="copyText(addressText(shippingAdress), 'Address')" matTooltip="Copy the address">
                          <mat-icon class="!text-[15px] !h-[15px] !w-[15px]">content_copy</mat-icon> Copy
                        </button>
                        <a class="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-gray-500 hover:bg-white hover:text-gray-900 transition-colors" [href]="mapsUrl(shippingAdress)" target="_blank" rel="noopener" matTooltip="Open in Google Maps">
                          <mat-icon class="!text-[15px] !h-[15px] !w-[15px]">map</mat-icon> Map
                        </a>
                        @if ((this.userInfo.userName == 'Nikhil5 Bhaskar' || this.userInfo.userName == 'Santosh Singh Dhami' || this.userInfo.userName == 'Debanjan techclouds')) {
                          <button class="ml-auto flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-amber-600 hover:bg-amber-50 transition-colors" (click)="onModifyShippingAddress()"
                            >
                            <mat-icon class="!text-[15px] !h-[15px] !w-[15px]">edit</mat-icon> Edit
                          </button>
                        }
                      </div>
                    } @else {
                      <div class="flex flex-col gap-1.5">
                        <input class="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" [(ngModel)]="editShipAddress1" placeholder="Address line 1" />
                        <input class="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" [(ngModel)]="editShipAddress2" placeholder="Address line 2" />
                        <input class="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" [(ngModel)]="editShipCity" placeholder="City" />
                        <input class="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" [(ngModel)]="editShipCountry" placeholder="Country" />
                        <input class="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" [(ngModel)]="editShipPostCode" placeholder="Post code" />
                        <input class="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" [(ngModel)]="editShipPhoneNo" placeholder="Phone" />
                      </div>
                      <div class="mt-3 flex items-center gap-2">
                        <button class="px-4 py-2 rounded-lg bg-amber-400 text-neutral-900 text-xs font-semibold hover:bg-amber-300" (click)="updateShippingAddress()">Save address</button>
                        <button class="px-4 py-2 rounded-lg bg-gray-100 text-gray-600 text-xs font-semibold hover:bg-gray-200" (click)="onCancelShippingAddressChanges()">Cancel</button>
                      </div>
                    }
                  </div>
                }

                <div class="rounded-xl border bg-gray-50/50 p-4 flex flex-col transition-colors"
                  [ngClass]="customerNoteDirty ? 'border-amber-300 bg-amber-50/40' : 'border-gray-100'">
                  <div class="flex items-start justify-between gap-2 mb-3">
                    <div class="flex items-center gap-2">
                      <mat-icon class="!text-[18px] !h-[18px] !w-[18px] !text-gray-400">sticky_note_2</mat-icon>
                      <div>
                        <h3 class="font-semibold text-gray-800 text-sm leading-tight">Customer notes</h3>
                        <p class="text-[11px] text-gray-400 leading-tight mt-0.5">Shown on the customer's order email</p>
                      </div>
                    </div>
                    @if (customerNoteDirty) {
                      <span class="flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 whitespace-nowrap">
                        <span class="h-1.5 w-1.5 rounded-full bg-amber-500"></span> Unsaved
                      </span>
                    } @else if (customerNoteJustSaved) {
                      <span class="flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 whitespace-nowrap">
                        <mat-icon class="!text-[13px] !h-[13px] !w-[13px]">check</mat-icon> Saved
                      </span>
                    }
                  </div>
                  <textarea
                    [(ngModel)]="order.orderNotes"
                    rows="5"
                    maxlength="2000"
                    placeholder="e.g. Please call before delivery, leave with neighbour, gate code 1234..."
                  class="w-full flex-1 min-h-[120px] px-3 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-800 placeholder:text-gray-300 resize-y focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400 transition-shadow"></textarea>
                  <div class="mt-2 flex items-center justify-between gap-2">
                    <span class="text-[11px] text-gray-400">{{ (order.orderNotes || '').length }}/2000</span>
                    <div class="flex items-center gap-2">
                      @if (customerNoteDirty && !savingCustomerNote) {
                        <button class="px-3 py-2 rounded-lg text-xs font-semibold text-gray-500 hover:text-gray-800 hover:bg-gray-100 transition-colors" (click)="onRevertCustomerNote()">Revert</button>
                      }
                      <button
                        class="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-neutral-900 text-amber-400 text-xs font-semibold hover:bg-neutral-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        [disabled]="!customerNoteDirty || savingCustomerNote"
                        (click)="onCustomerOrderNoteUpdate()">
                        @if (savingCustomerNote) {
                          <mat-spinner diameter="14"></mat-spinner> Saving...
                        } @else {
                          <mat-icon class="!text-[16px] !h-4 !w-4">save</mat-icon> Save note
                        }
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Coupons -->
          @if (appliedCouponCodes.length > 0 || (order.orderStatus === 'PENDING' || order.orderStatus === 'ON HOLD' || order.orderStatus === 'PENDING COLLECTION')) {
            <div class="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <div class="flex items-center gap-2 mb-3">
                <mat-icon class="!text-[20px] !h-5 !w-5 !text-amber-500">sell</mat-icon>
                <h2 class="font-semibold text-gray-800">Discount coupons</h2>
              </div>
              @if (appliedCouponCodes.length > 0) {
                <div class="flex flex-wrap gap-2 mb-3">
                  @for (ccode of appliedCouponCodes; track $index) {
                    <span class="inline-flex items-center gap-1 text-xs font-semibold pl-3 pr-2 py-1.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-100">
                      {{ ccode.couponCode }}: {{ ccode.couponValue }} {{ccode.couponPerc ? '%' : '£'}} off
                      @if (order.orderStatus === 'PENDING' || order.orderStatus === 'ON HOLD' || order.orderStatus === 'PENDING COLLECTION') {
                        <button class="h-4 w-4 rounded-full hover:bg-emerald-200 flex items-center justify-center" (click)="onRemoveCouponCode(ccode, $index)">
                          <mat-icon class="!text-[12px] !h-3 !w-3">close</mat-icon>
                        </button>
                      }
                    </span>
                  }
                </div>
              }
              @if (order.orderStatus === 'PENDING' || order.orderStatus === 'ON HOLD' || order.orderStatus === 'PENDING COLLECTION') {
                <div class="flex flex-wrap items-center gap-2">
                  <input [(ngModel)]="enteredCouponCode" class="px-3 py-2 rounded-xl border border-gray-200 bg-gray-50 text-sm w-64 focus:outline-none focus:ring-2 focus:ring-amber-400" name="coupon-code" type="text" placeholder="Coupon code" />
                  <button class="px-4 py-2 rounded-xl bg-neutral-900 text-amber-400 text-xs font-semibold hover:bg-neutral-800 transition-colors" (click)="onApplyCouponCode()">Apply</button>
                </div>
                @if (couponError) {
                  <div class="text-xs font-medium text-red-500 mt-1.5">{{ couponError }}</div>
                }
              }
            </div>
          }

          <!-- Refund -->
          @if( order.orderStatus === 'PROCESSING' || order.orderStatus === 'COMPLETED' || order.orderStatus === 'DELAYED COMPLETION' || order.orderStatus === 'REFUNDED' || order.orderStatus === 'PARTIALLY REFUNDED' || order.orderStatus === 'VOID') {
            <div class="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <div class="flex items-center gap-2 mb-4">
                <mat-icon class="!text-[20px] !h-5 !w-5 !text-amber-500">currency_pound</mat-icon>
                <h2 class="font-semibold text-gray-800">Refunds</h2>
              </div>
              @if (refundHistory && refundHistory.length > 0) {
                <div class="text-sm">
                  <h3 class="font-semibold text-gray-700 mb-3">Refund history</h3>
                  @for (item of refundHistory; track $index) {
                    <div class="flex flex-col">
                      <p class="text-base">
                        Refund Id:  <b>{{item.chargeId}}</b>
                      </p>
                      <p class="text-base">
                        <b>£{{item.amount}}</b> refunded on <b>{{item.refundDate | date : "MMM d, y"}}</b>. - <b>{{item.status}}</b>
                      </p>
                      @if (item.stockAdjusted && item.status === 'SUCCESS') {
                        <label class="text-xs text-neutral-500">Stock Adjusted</label>
                        @for (rI of item.refunditems; track $index) {
                          <div class="border-[1px] p-2 flex flex-row">
                            <div class="basis-3/4 text-xs">
                              {{rI.productVariant.sku}}
                            </div>
                            <div class="basis-1/4 text-xs">
                              {{rI.quantity}}
                            </div>
                          </div>
                        }
                      }
                    </div>

                  }
                </div>
              }
              @if((order.orderStatus === 'PROCESSING' || order.orderStatus === 'COMPLETED' || order.orderStatus === 'DELAYED COMPLETION' || order.orderStatus === 'REFUNDED' || order.orderStatus === 'PARTIALLY REFUNDED' || order.orderStatus === 'VOID') && order.netAmount - totalRefundedAmount > 0 && !gatewayRefundsEnabled) {
                <div class="mt-5 rounded-xl border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-800">
                  Refunds for {{ order.paymentMethod === 'TEYA' ? 'Teya (card)' : order.paymentMethod === 'PAYPAL' ? 'PayPal' : order.paymentMethod }} orders are switched off in Site Settings. Refund this order in the payment provider's own portal.
                </div>
              }
              @if((order.orderStatus === 'PROCESSING' || order.orderStatus === 'COMPLETED' || order.orderStatus === 'DELAYED COMPLETION' || order.orderStatus === 'REFUNDED' || order.orderStatus === 'PARTIALLY REFUNDED' || order.orderStatus === 'VOID') && order.netAmount - totalRefundedAmount > 0 && gatewayRefundsEnabled) {
                <div class="mt-5 rounded-xl border border-gray-200 overflow-hidden">
                  <div class="flex flex-wrap items-center justify-between gap-2 px-5 py-3 bg-gray-50 border-b border-gray-100">
                    <div class="font-semibold text-gray-800">New refund</div>
                    <div class="text-xs font-semibold px-3 py-1 rounded-full bg-emerald-50 text-emerald-700">
                      Available to refund: £{{ order.netAmount - totalRefundedAmount | number:'1.2-2' }}
                    </div>
                  </div>

                  <div class="p-5 flex flex-col gap-5">

                    <!-- Full / partial -->
                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <button type="button"
                        class="text-left rounded-xl border-2 px-4 py-3 transition-all"
                        [ngClass]="refundType === 'FULL' ? 'border-amber-400 bg-amber-50' : 'border-gray-200 hover:border-gray-300'"
                        (click)="refundType = 'FULL'; totalRefundAmount = order.netAmount - totalRefundedAmount;">
                        <div class="flex items-center gap-2 font-semibold text-gray-900">
                          <mat-icon class="!text-[18px] !h-[18px] !w-[18px]" [ngClass]="refundType === 'FULL' ? '!text-amber-500' : '!text-gray-400'">{{ refundType === 'FULL' ? 'radio_button_checked' : 'radio_button_unchecked' }}</mat-icon>
                          Full refund
                        </div>
                        <div class="text-xs text-gray-500 mt-0.5 ml-[26px]">Refund everything that's left on this order</div>
                      </button>
                      <button type="button"
                        class="text-left rounded-xl border-2 px-4 py-3 transition-all"
                        [ngClass]="refundType === 'PARTIAL' ? 'border-amber-400 bg-amber-50' : 'border-gray-200 hover:border-gray-300'"
                        (click)="refundType = 'PARTIAL'; adjustStock ? onCustomStockAdjustmentRefundAmt() : onPartiallyRefundAmtChange()">
                        <div class="flex items-center gap-2 font-semibold text-gray-900">
                          <mat-icon class="!text-[18px] !h-[18px] !w-[18px]" [ngClass]="refundType === 'PARTIAL' ? '!text-amber-500' : '!text-gray-400'">{{ refundType === 'PARTIAL' ? 'radio_button_checked' : 'radio_button_unchecked' }}</mat-icon>
                          Partial refund
                        </div>
                        <div class="text-xs text-gray-500 mt-0.5 ml-[26px]">Choose an amount, or pick the items to refund</div>
                      </button>
                    </div>

                    <!-- Restock -->
                    <label class="flex items-start gap-3 rounded-xl border px-4 py-3 cursor-pointer transition-colors"
                      [ngClass]="adjustStock ? 'border-amber-300 bg-amber-50/60' : 'border-gray-200 hover:bg-gray-50'">
                      <input type="checkbox" class="mt-1 h-4 w-4 accent-amber-500" [(ngModel)]="adjustStock" (ngModelChange)="onAdjustQuantityCheck()" />
                      <div>
                        <div class="font-semibold text-gray-800 text-sm">Restock refunded items</div>
                        <div class="text-xs text-gray-500">
                          {{ refundType === 'FULL' ? 'Every item on the order goes back into stock.' : 'Put the quantities you refund back into stock.' }}
                        </div>
                      </div>
                    </label>

                    @if (refundType === 'PARTIAL') {
                      @if (!adjustStock) {
                        <div>
                          <label class="block text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Refund amount</label>
                          <div class="flex flex-wrap items-center gap-3">
                            <div class="relative w-full sm:w-56">
                              <span class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 font-semibold">£</span>
                              <input type="number" [max]="order.netAmount - totalRefundedAmount" [min]="0" step="0.01"
                                class="w-full pl-7 pr-3 py-2.5 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none text-sm font-semibold"
                                [(ngModel)]="partiallyRefundAmtWithoutStock" (ngModelChange)="onPartiallyRefundAmtChange()" />
                            </div>
                            <div class="flex gap-1.5">
                              @for (pct of [25, 50, 75]; track pct) {
                                <button type="button" class="px-3 py-1.5 rounded-full border border-gray-200 text-xs font-semibold text-gray-600 hover:border-amber-300 hover:bg-amber-50 transition-colors"
                                  (click)="partiallyRefundAmtWithoutStock = roundMoney((order.netAmount - totalRefundedAmount) * pct / 100); onPartiallyRefundAmtChange()">
                                  {{ pct }}%
                                </button>
                              }
                            </div>
                          </div>
                        </div>
                      } @else {
                        <div>
                          <div class="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Items to refund</div>
                          <div class="rounded-xl border border-gray-100 overflow-hidden">
                            <table class="w-full text-sm">
                              <thead>
                                <tr class="text-left text-xs font-semibold text-gray-400 uppercase tracking-wide bg-gray-50">
                                  <th class="px-4 py-2.5">Item</th>
                                  <th class="px-3 py-2.5 text-center">Ordered</th>
                                  <th class="px-3 py-2.5 text-center">Refund qty</th>
                                  <th class="px-3 py-2.5 text-right">Amount</th>
                                </tr>
                              </thead>
                              <tbody>
                                @for (item of refundItems; track $index) {
                                  <tr class="border-t border-gray-50" [ngClass]="item.refundQty > 0 ? 'bg-amber-50/40' : ''">
                                    <td class="px-4 py-2.5">
                                      <div class="font-medium text-gray-900">{{ item.productName }}</div>
                                      <div class="text-[11px] text-gray-400">£{{ item.unitPrice }} each</div>
                                    </td>
                                    <td class="px-3 py-2.5 text-center text-gray-600">{{ item.originalQty }}</td>
                                    <td class="px-3 py-2.5 text-center">
                                      <input type="number" min="0" [max]="item.originalQty"
                                        class="w-20 px-2 py-1.5 rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none text-sm text-center font-semibold"
                                        [(ngModel)]="item.refundQty" (ngModelChange)="onRefundStockChanage($index)" />
                                    </td>
                                    <td class="px-3 py-2.5 text-right font-semibold" [ngClass]="item.refundAmt > 0 ? 'text-gray-900' : 'text-gray-300'">
                                      £{{ (item.refundAmt || 0) | number:'1.2-2' }}
                                    </td>
                                  </tr>
                                }
                              </tbody>
                            </table>
                          </div>
                          <div class="mt-3">
                            <label class="block text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Extra amount (e.g. shipping or goodwill)</label>
                            <div class="relative w-full sm:w-56">
                              <span class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 font-semibold">£</span>
                              <input type="number" step="0.01"
                                class="w-full pl-7 pr-3 py-2.5 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none text-sm font-semibold"
                                [(ngModel)]="refundCustomStockAdjustmentAmt" (ngModelChange)="onCustomStockAdjustmentRefundAmt()" />
                            </div>
                            @if (refuntCustomAmtErr) {
                              <div class="text-xs text-red-500 mt-1">The total refund can't be more than the order total.</div>
                            }
                          </div>
                        </div>
                      }
                    }

                    <!-- Reason -->
                    <div>
                      <label class="block text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Reason</label>
                      <textarea rows="3" [(ngModel)]="refundReason" placeholder="Why is this order being refunded? (kept on the refund record)"
                      class="w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none text-sm resize-y"></textarea>
                    </div>
                  </div>

                  <!-- Summary + action -->
                  <div class="flex flex-wrap items-center justify-between gap-4 px-5 py-4 bg-gray-50 border-t border-gray-100">
                    <div>
                      <div class="text-xs font-semibold text-gray-400 uppercase tracking-wide">Total refund</div>
                      <div class="text-2xl font-bold text-gray-900">£{{ totalRefundAmount | number:'1.2-2' }}</div>
                      @if (refundBlockedReason) {
                        <div class="text-xs text-amber-700 font-medium mt-0.5">{{ refundBlockedReason }}</div>
                      }
                    </div>
                    <button
                      class="flex items-center gap-2 px-6 py-3 rounded-xl bg-orange-500 text-white text-sm font-semibold uppercase tracking-wide shadow-sm hover:bg-orange-600 active:scale-[0.98] transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-orange-500"
                      [disabled]="!!refundBlockedReason" (click)="onRefundClick()">
                      <mat-icon class="!text-[18px] !h-[18px] !w-[18px]">undo</mat-icon> Refund order
                    </button>
                  </div>
                </div>
              }

            </div>
          }

        </div>

        <!-- Order notes -->
        <div class="xl:col-span-1 bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden xl:sticky xl:top-20">
          <div class="flex items-center justify-between px-5 py-4 border-b border-gray-100">
            <div class="flex items-center gap-2">
              <mat-icon class="!text-[20px] !h-5 !w-5 !text-amber-500">forum</mat-icon>
              <h2 class="font-semibold text-gray-800">Order notes</h2>
              @if (orderNotes && orderNotes.length > 0) {
                <span class="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{{ orderNotes.length }}</span>
              }
            </div>
            <button (click)="showEditForm = !showEditForm"
              class="h-8 w-8 rounded-full bg-amber-400 hover:bg-amber-300 text-neutral-900 flex items-center justify-center shadow-sm transition-colors"
              [matTooltip]="showEditForm ? 'Close' : 'Add a note'">
              <mat-icon class="!text-[20px] !h-5 !w-5">{{ showEditForm ? 'close' : 'add' }}</mat-icon>
            </button>
          </div>

          @if (showEditForm) {
            <div class="p-4 border-b border-gray-100 bg-amber-50/50">
              <textarea [(ngModel)]="orderNote" rows="3" placeholder="Write a note..." class="w-full px-3 py-2 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"></textarea>
              <div class="mt-2 flex gap-2">
                <button (click)="onOrderNoteCreation()" class="px-4 py-2 rounded-xl bg-neutral-900 text-amber-400 text-xs font-semibold hover:bg-neutral-800 transition-colors">Save note</button>
                <button class="px-4 py-2 rounded-xl bg-gray-100 text-gray-600 text-xs font-semibold hover:bg-gray-200" (click)="showEditForm = !showEditForm">Cancel</button>
              </div>
            </div>
          }

          @if (orderNotes && orderNotes.length > 0) {
            <div class="flex items-center justify-between px-5 py-2.5 border-b border-gray-100 text-xs text-gray-500 bg-gray-50/60">
              <span>{{ visibleNotes.length }} of {{ orderNotes.length }} shown</span>
              <label class="flex items-center gap-1.5 cursor-pointer font-medium">
                <input type="checkbox" class="h-3.5 w-3.5 accent-amber-500" [(ngModel)]="showSystemNotes" />
                System updates
              </label>
            </div>
          }
          <div class="max-h-[75vh] overflow-y-auto p-5">
            @if (visibleNotes.length > 0) {
              <div class="relative pl-7">
                <div class="absolute left-[9px] top-2 bottom-2 w-px bg-gray-200"></div>
                @for (orderNote of visibleNotes; track orderNote.id) {
                  @if (noteKind(orderNote.orderNote); as kind) {
                    <div class="relative pb-5 last:pb-0 group">
                      <div class="absolute -left-7 top-0 h-[19px] w-[19px] rounded-full flex items-center justify-center ring-4 ring-white" [ngClass]="kind.dot">
                        <mat-icon class="!text-[12px] !h-3 !w-3 !text-white">{{ kind.icon }}</mat-icon>
                      </div>
                      <div class="rounded-xl border px-3.5 py-2.5" [ngClass]="kind.card">
                        @if (kind.key === 'delivery') {
                          <div class="text-[10px] font-bold uppercase tracking-wide text-amber-700 mb-1">Delivery notes</div>
                          <div class="text-sm text-gray-800 whitespace-pre-line pr-5">{{ orderNote.orderNote.substring(15) }}</div>
                        } @else if (kind.key === 'status') {
                          <div class="flex items-center gap-2 flex-wrap">
                            <span class="text-xs text-gray-500">Status changed to</span>
                            <span class="text-xs font-semibold px-2.5 py-0.5 rounded-full capitalize" [ngClass]="statusPill(noteStatus(orderNote.orderNote))">{{ noteStatus(orderNote.orderNote) | lowercase }}</span>
                          </div>
                        } @else {
                          <div class="text-sm whitespace-pre-line pr-5" [ngClass]="kind.key === 'note' ? 'text-gray-800' : 'text-gray-600'">{{ orderNote.orderNote }}</div>
                        }
                        <div class="mt-1.5 flex flex-row justify-between items-center text-[11px] text-gray-400">
                          <span [matTooltip]="(orderNote.orderNoteDate | date : 'MMM d, y HH:mm:ss') ?? ''">{{ orderNote.orderNoteDate | date : "MMM d, h:mm a" }}</span>
                          <span class="font-semibold text-gray-500">{{ orderNote.orderNoteUser?.firstname || 'System' }}</span>
                        </div>
                        <button class="absolute top-2 right-2 h-6 w-6 rounded-full opacity-0 group-hover:opacity-100 hover:bg-red-50 text-red-500 flex items-center justify-center transition-opacity"
                          matTooltip="Delete" (click)="onOrderNoteDelete(orderNote.id)">
                          <mat-icon class="!text-[15px] !h-[15px] !w-[15px]">delete</mat-icon>
                        </button>
                      </div>
                    </div>
                  }
                }
              </div>
            } @else {
              <div class="text-sm text-gray-400 text-center py-8">{{ orderNotes && orderNotes.length ? 'Only system updates - tick "System updates" to see them' : 'No notes yet' }}</div>
            }
          </div>
        </div>
      </div>
    </div>
  </div>
}
</app-main-layout>
